import React, { Children, ReactElement, ReactNode, cloneElement } from 'react';
import { cn } from '@restworld/utils-common';

import { Logo } from '@restworld/ui-ds-base';

import { Navbar } from './navigation-bar-styled';
import type { NavbarBackgroundType, NavbarStyleType } from './navigation-bar.types';
import { NonAuthenticatedNav, MobileOverlayNav } from './navigation-bar.components';
import { LinkBase } from '../../design-system';

export const NavigationBar = (props: NavigationBarProps) => {
  const {
    navElement,
    justify,
    background = 'white',
    children,
    isMobileDropdown,
    testId,
    additionalContentEl,
    ...rest
  } = props;
  const navigationComponent = navElement
    ? // `navElement` is often a Fragment (`<>…</>`). Cloning `background` onto a Fragment warns
      // ("Invalid prop `background` supplied to React.Fragment") and doesn't reach its children
      // anyway. Only inject the prop into elements that can accept it; pass Fragments through.
      Children.map(navElement, (child) =>
        React.isValidElement(child) && child.type !== React.Fragment
          ? cloneElement(child as ReactElement<{ background?: NavbarBackgroundType }>, { background })
          : child
      )
    : null;
  const commonContainer = (
    <div className="grid grid-cols-3 w-full px-4 lg:min-h-[97px]">
      <div className="flex items-center">
        <LinkBase link="/" newTab={false}>
          <NavLogo background={background} />
        </LinkBase>
      </div>
      {navigationComponent}
    </div>
  );
  // --- //
  const mobileNav = (
    <div className="block lg:hidden max-w-[100vw]">
      <Navbar background={background} testId={`mobile-${testId}`} additionalContentEl={additionalContentEl} {...rest}>
        {isMobileDropdown ? commonContainer : navigationComponent}
        {children}
      </Navbar>
    </div>
  );
  const desktopNav = (
    <div className="hidden lg:block max-w-[100vw]">
      <Navbar background={background} testId={`desktop-${testId}`} additionalContentEl={additionalContentEl} {...rest}>
        {commonContainer}
        {children}
      </Navbar>
    </div>
  );
  return (
    <>
      {mobileNav}
      {desktopNav}
    </>
  );
};
export default NavigationBar;
export interface NavigationBarProps extends NavbarStyleType {
  navElement?: ReactElement<any>;
  justify?: 'start' | 'between'; // default start
  children?: ReactElement<any>;
  isMobileDropdown?: boolean;
  testId?: string;
  additionalContentEl?: ReactNode;
}
export const NavLogo = ({ background, isSmallLogo }: { background?: NavbarBackgroundType; isSmallLogo?: boolean }) => {
  const colorClass = background === 'white' ? 'text-purple-700' : 'text-white';

  return (
    <LogoWrapper $isSmallLogo={isSmallLogo ?? false}>
      <Logo variant="horizontal" title="restworld" className={`hidden h-8 w-auto lg:block ${colorClass}`} />
      <Logo variant="mark" title="restworld" className={`h-9 w-auto lg:hidden ${colorClass}`} />
    </LogoWrapper>
  );
};
const LogoWrapper = ({
  $isSmallLogo,
  className,
  ...props
}: { $isSmallLogo: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('shrink-0 pl-2', $isSmallLogo ? 'min-w-[36px]' : 'min-w-[40px]', className)} {...props} />
);
export { NonAuthenticatedNav, MobileOverlayNav };
export * from './navigation-bar.components/impression-component';
export { useMobileNavbarCrossingAnimation } from './hooks/use-navbar-crossing-animation';
